import { log } from '@/log'
import { CartScopeEq } from '@/models/cart/group/scope'
import { DateWithoutHour } from '@/models/date-time/date-without-hour'
import { CreditcardPaymentProviderServiceForPurchase } from '@/services/payment/creditcard-payment-provider/creditcard-payment-provider-service'
import { CreditcardPayment } from '@/services/payment/types'
import { adCodeStore } from '@/util/adcode/store'
import { Checkout, CheckoutKey, CheckoutStore } from '@spa/components/pages/order/order/models/checkout-store'
import { OrderPurchaseResult, OrderService } from '@spa/services/order'
import { Delivery, isCreditCard, Payment } from '@spa/store/modules/orderConfirm/types'
import { User } from '@spa/store/modules/user/types'
import { chain, Either, left, right } from 'fp-ts/lib/Either'
import { fromNullable, Option, Some } from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

const commonErrorMessage = 'エラーが発生しました。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'

/**
 * 注文を実行するに当たっての事前に準備された情報一式をまとめたモデル
 * ここで「注文を実行」とはユーザーが注文ボタンを押した後に実行されるすべての手続きを指し、
 * 「事前に準備された情報」は注文ボタンが押されるまでに取得された情報を指す。
 * FrontのAPIおよびEngineへの注文リクエストを注文と呼んでいるわけではないことに注意。
 */
export class PurchasePreparationState {
  constructor(
    public readonly checkoutStore: CheckoutStore,
    public readonly payment: Payment,
    // payment_auth_tokenは注文ボタン押下後に変更されうるのでアクセスしてはならない
    public readonly user: Omit<User, 'payment_auth_token'>,
    public readonly delivery: Delivery,
    public readonly deliveryDate: DateWithoutHour | null,
    public readonly deliveryTimeSlot: string,
    public readonly appliedCouponId: string | null,
    public readonly couponDiscountPrice: Option<number>,
    public readonly appliedPoint: number,
    public readonly serviceFeePercent: number | null,
    public readonly scope: CartScopeEq
  ) {}

  /**
   * ユーザーサイドであるかどうかというよりは、EngineへPOST commerce_purchaseする前に
   * 事前準備が必要であるかという関心のみ存在するように見える
   *
   * 実際、ユーザーサイドであり、かつisFullyDiscountedExceptForPaymentである場合には
   * atoneやpaidyのモーダル起動をスキップするという利用のされ方だが、
   * 実際にはクレジットカードのtoken取得といった事前処理全般が不要なのでスキップして良いはず
   */
  get paymentTypeIsUserSide(): boolean {
    return ['atone', 'paidy'].some(p => p === this.payment.payment_type)
  }

  /**
   * server側でも同様の条件により、支払い方法をnoneに強制セットしている。
   * @see src/Parameter/CommercePurchase/Post.php #buildRequestWithPaymentTypeNoneIfSomeSatisfied
   * もっとも、noneはclientがリクエストすべきことで、serverは受け取ったnoneをバリデーションするに止め、支払い方法の強制セットを控えるべきという考え方もある。
   * その辺りはserverにロジックを収納していく過程で検討したい。
   *
   * またこのメソッドは、2022/01/17現在、client上で支払い方法をnoneにセットしているわけ「ではない」。
   * 本来はclientでの表示の段階で、ユーザにnoneであることを見せる方が望ましいようにも思う。
   * その要望があれば今後対応を検討する。なおその時、クーポンをつけたり外したりして、noneでなくなった時にどう見せるか（元の支払い方法を記憶していてそこに戻るか）
   * ということも考慮しなければならない。
   */
  get isFullyDiscountedExceptForPayment(): boolean {
    return this.checkoutStore.totalPrice <= this.checkoutStore.commission
  }

  get purchaseParamsBase() {
    const delivery = this.delivery!
    const deliveryDate = this.deliveryDate
    const deliveryTimeSlot = this.deliveryTimeSlot
    const maybeAdCode = adCodeStore.read()
    const couponId = this.appliedCouponId
    const discount = this.couponDiscountPrice.getOrElse(0)
    const referral = this.user.referral || null

    return {
      delivery,
      deliveryDate,
      deliveryTimeSlot,
      ...fromNullable(this.delivery)
        .chain(d => fromNullable(d.unattendedDelivery))
        .fold<{ unattendedDelivery: string | null }>(
          { unattendedDelivery: null },
          unattendedDelivery => ({ unattendedDelivery })
        ),
      ...maybeAdCode.map<{ adCode?: string }>(adCode => ({ adCode })).getOrElse({}),
      ...couponId && { couponId },
      // Discount value validation is not interested here, and validated on the server-side.
      ...discount && { discount },
      cartScope: this.scope,
      referral,
    }
  }

  validateCheckoutKeysMustBeSingle(checkoutKeys: CheckoutKey[]): Either<Error, CheckoutKey> {
    return checkoutKeys.length === 1 ? right(checkoutKeys[0]) : left(new Error('CheckoutKeys must be single'))
  }

  validateCheckoutExistence(checkoutKey: CheckoutKey): Either<Error, Checkout> {
    return this.checkoutStore.findByKey(checkoutKey).fold(
      left(new Error('Checkout not found')),
      checkout => right(checkout)
    )
  }
}

type PurchaseExecutionProcess = () => Promise<OrderPurchaseResult[]>

const createPurchaseExecutionProcessDefault = async (
  orderService: OrderService,
  state: PurchasePreparationState,
  checkoutKeys: CheckoutKey[]
): Promise<Either<string, PurchaseExecutionProcess>> => {
  const checkouts = checkoutKeys
    .map(key => state.checkoutStore.findByKey(key))
    .filter((maybeCheckout): maybeCheckout is Some<Checkout> => maybeCheckout.isSome())
    .map(checkout => checkout.value)

  if (checkoutKeys.length !== checkouts.length) {
    log.error({ checkoutKeys, checkouts }, 'at least one checkout object is not found')

    return Promise.resolve(left(commonErrorMessage))
  }

  return createPurchaseExecutionProcess(orderService, state, { checkouts })
}

const createPurchaseExecutionProcessCreditCard = async (
  cardPaymentProvider: CreditcardPaymentProviderServiceForPurchase,
  orderService: OrderService,
  state: PurchasePreparationState,
  checkoutKeys: CheckoutKey[]
): Promise<Either<string, PurchaseExecutionProcess>> => {
  const createCreditCardPayment = async (): Promise<Either<Error, CreditcardPayment>> => {
    if (!isCreditCard(state.payment)) {
      return left(new Error(`payment is not for creditcard`))
    }
    const { token, lastCreditDate, numberLast4, cardType } = state.payment

    return await cardPaymentProvider.createPaymentPayload(
      state.payment,
      {
        id: token,
        lastCreditDate,
        // 2025/05時点で支払い時にセキュリティコード入力を要求することはない
        // また、3Dセキュア化が済んだので、おそらく今後もそうなることはない
        // 既存の挙動にならって固定で空文字を入れる
        // securityCodeが必要になれば、paymentオブジェクトに含めてここまで伝える
        securityCode: '',
        numberLast4,
        cardType,
      },
      state.user
    )
  }

  return pipe(
    state.validateCheckoutKeysMustBeSingle(checkoutKeys),
    chain(checkoutKey => state.validateCheckoutExistence(checkoutKey))
  ).fold(
    (error) => {
      log.error(`createPurchaseExecutionProcessCreditCard validation error: ${error.message}`)

      return Promise.resolve(left(commonErrorMessage))
    },
    async checkout => await createPurchaseExecutionProcess(
      orderService,
      state,
      { checkouts: [checkout] },
      false,
      createCreditCardPayment
    )
  )
}

const createPurchaseExecutionProcessAtone = async (
  orderService: OrderService,
  state: PurchasePreparationState,
  checkout: Checkout,
  provisionalOrderIdForSingleOrder: string,
  externalPaymentId: string,
  paymentAuthToken: string
): Promise<Either<string, PurchaseExecutionProcess>> => {
  const createPayment = async (): Promise<Either<Error, Payment>> =>
    right({
      ...state.payment,
      variableResource: {
        ...state.payment.variableResource,
        ... {
          externalPaymentId,
          paymentAuthToken,
        },
      },
    })

  return createPurchaseExecutionProcess(
    orderService,
    state,
    {
      checkouts: [checkout],
      provisionalOrderIdForSingleOrder,
    },
    false,
    createPayment
  )
}

const createPurchaseExecutionProcessPaidy = async (
  orderService: OrderService,
  state: PurchasePreparationState,
  checkout: Checkout,
  provisionalOrderIdForSingleOrder: string,
  externalPaymentId: string
): Promise<Either<string, PurchaseExecutionProcess>> => {
  const createPayment = async (): Promise<Either<Error, Payment>> =>
    right({
      ...state.payment,
      variableResource: {
        ...state.payment.variableResource,
        ... {
          externalPaymentId,
        },
      },
    })

  return createPurchaseExecutionProcess(
    orderService,
    state,
    {
      checkouts: [checkout],
      provisionalOrderIdForSingleOrder,
    },
    false,
    createPayment
  )
}

const createPurchaseExecutionProcess = async (
  orderService: OrderService,
  state: PurchasePreparationState,
  params: {
    checkouts: Checkout[],
    provisionalOrderIdForSingleOrder?: string,
  },
  parallel: boolean = true,
  beforePaymentAction: (() => Promise<Either<Error, Payment>>) | null = null
): Promise<Either<string, PurchaseExecutionProcess>> => {
  const onValidationFailed = (error: unknown): Promise<Either<string, never>> => {
    // TODO: サーバーにエラーを送信したい
    log.error(`Validation on executePurchase failed`, { error })

    return Promise.resolve(left(commonErrorMessage))
  }

  if (params.checkouts.length > 1 && params.provisionalOrderIdForSingleOrder) {
    return onValidationFailed(
      new Error('provisionalOrderIdForSingleOrder can be specified only when checkout is single.')
    )
  }

  const orders = params.checkouts.map(c => ({
    ...c.order,
    ...params.provisionalOrderIdForSingleOrder ? {
      // atone, ペイディでは事前に払い出した注文IDを利用しなければならない
      id: params.provisionalOrderIdForSingleOrder,
    } : {},
  }))
  const payment = state.payment
  payment.variableResource = {
    ...payment.variableResource,
    ...{ user: state.user },
  }

  // 単一の注文しか扱えないpaymentで複数checkoutが実行されようとしていたらエラー
  if (params.checkouts.length > 1 && payment.apiPayment && !payment.apiPayment.availableForMultipleOrders) {
    return onValidationFailed(
      new Error('provisionalOrderIdForSingleOrder can be specified only when checkout is single.')
    )
  }

  /**
   * TODO:
   * すでにorder-estimateのtupleとして機能できるCheckoutが存在するのでorderServiceにもCheckout[]を渡せるようにしたい
   * が、いったん修正をOrder.vueに閉じさせるためにOrderService側のI/Fに合わせて変換する
   */
  const purchaseEstimates = params.checkouts.map(c => c.estimate)
  orders.forEach((o, index: number) => {
    const discountDetails = purchaseEstimates[index] && purchaseEstimates[index].price
      && purchaseEstimates[index].price.discount

    o.discount = {
      coupon: discountDetails !== undefined ? discountDetails.coupon.amount : 0,
      bundle: discountDetails !== undefined ? discountDetails.bundle.amount : 0,
    }

    o.estimatedPoints = purchaseEstimates[index].point.amount || 0
  })

  return Promise.resolve(right(async () => {
    return await orderService.purchase(
      {
        orders,
        payment: {
          ...payment,
          ...(state.appliedPoint > 0) ? {
            point: {
              kind: 'point',
              amount: state.appliedPoint,
            },
          } : {},
        },
        ...state.purchaseParamsBase,
        ...(state.serviceFeePercent !== null) ? {
          serviceFee: {
            percent: state.serviceFeePercent,
          },
        } : {},
      },
      parallel,
      beforePaymentAction
    )
  }))
}

export const createPurchaseExecutionProcessFunctions = {
  creditCard: createPurchaseExecutionProcessCreditCard,
  atone: createPurchaseExecutionProcessAtone,
  paidy: createPurchaseExecutionProcessPaidy,
  default: createPurchaseExecutionProcessDefault,
}
